import { Button } from '@neo/primitives/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { ChevronDown, History } from 'lucide-react'
import { useState } from 'react'
import { useVersionView } from '../../hooks/use-version-view'
import { useVersionsQuery } from '../../queries'
import type { FlowData, FlowVersion } from '../../types'
import { checkFlowUnsavedChangesForNavigation } from '../../utils'
import { isAiAgentV2Type } from '../../utils/ai-agent-v29'
import { FlowVersionList } from './flow-version-list'
import { useEditVersionDescription } from './use-edit-version-description'
import { useMakeVersionActive } from './use-make-version-active'
import { sortVersionsDesc } from './version-utils'

interface FlowVersionHistoryProps {
  flowData?: FlowData
  isNew: boolean
}

export const FlowVersionHistory = ({ flowData, isNew }: FlowVersionHistoryProps) => {
  const phloID = flowData?.phlo?.id
  const isAgentFlow = !!flowData?.nodes?.some((node) => isAiAgentV2Type(node.type))

  const { viewingVersionId, viewVersion } = useVersionView()

  const [open, setOpen] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  // Only fetch once the dropdown is opened — avoids a versions call for every flow load.
  const { data, isLoading, isError, refetch } = useVersionsQuery(phloID, open)
  const { requestMakeActive, dialog: makeActiveDialog } = useMakeVersionActive(phloID, () =>
    setOpen(false),
  )
  const { requestEdit, dialog: editSummaryDialog } = useEditVersionDescription(phloID)

  // Versioning is an agent (ai_agent_v2) feature and needs a saved flow.
  if (isNew || !phloID || !isAgentFlow) return null

  const versions = data?.versions ? sortVersionsDesc(data.versions) : []

  const handleViewVersion = (version: FlowVersion) => {
    // Swapping the canvas to another version discards in-progress flow edits (nodes,
    // edges, settings) — guard with the flow-level check, not just the open sidebar form.
    checkFlowUnsavedChangesForNavigation(true, () =>
      viewVersion(version.is_active ? null : version.id),
    )
  }

  return (
    <>
      {/* Viewing a version is driven by the URL (`?version`), not the menu: closing the
          dropdown (e.g. to inspect a node's config on the canvas) does NOT exit the view.
          It's left via "Back to current", a switch, or navigating away. */}
      <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label="Version history"
            data-testid="button-version-history"
          >
            <History className="h-4 w-4" />
            <ChevronDown className="ml-1 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[328px] p-0">
          <FlowVersionList
            versions={versions}
            createdByName={flowData?.phlo?.created_by?.name}
            selectedVersionId={viewingVersionId}
            isLoading={isLoading}
            isError={isError || isRetrying}
            isRetrying={isRetrying}
            onRetry={async () => {
              setIsRetrying(true)
              try {
                await refetch()
              } finally {
                setIsRetrying(false)
              }
            }}
            onViewVersion={handleViewVersion}
            onSwitch={requestMakeActive}
            onEditSummary={requestEdit}
          />
        </DropdownMenuContent>
      </DropdownMenu>

      {makeActiveDialog}
      {editSummaryDialog}
    </>
  )
}
